import { useState } from 'react';
import { Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SearchRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getCEP } from '../../../api/auxiliares';

const formatCep = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return digits;
};

/**
 * Campo de CEP com botão "Buscar CEP" ao lado.
 *
 * @param {Object} props
 * @param {string} props.cepValue - Valor atual do CEP
 * @param {Function} props.onCepChange - Callback quando o CEP muda
 * @param {Function} props.onAddressLoad - Callback quando o endereço é carregado (recebe { endereco, cidade, estado })
 * @param {Function} props.handleBlur - Função handleBlur do Formik (opcional)
 * @param {boolean} props.required - Se o campo é obrigatório (padrão: true)
 * @param {boolean} props.error / props.helperText - Estado de validação do Formik (opcional)
 */
export default function CepForm({
  cepValue,
  onCepChange,
  onAddressLoad,
  handleBlur,
  required = true,
  error,
  helperText,
}) {
  const { t } = useTranslation();
  const [loadingCep, setLoadingCep] = useState(false);

  const handleBuscarCep = () => {
    if (cepValue && cepValue.length === 9) {
      setLoadingCep(true);
      buscarCEP(cepValue);
    }
  };

  const buscarCEP = (value) => {
    const cepFormat = value.replace(/\D/g, '');
    handleCEP(cepFormat);
  };

  const handleCEP = _.debounce(async (cepFormat) => {
    if (cepFormat.length === 8) {
      try {
        const endereco = await getCEP(cepFormat);

        const data = {
          endereco: endereco.data.data.logradouro,
          cidade: endereco.data.data.municipio,
          estado: endereco.data.data.uf,
        };
        onAddressLoad(data);
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      } finally {
        setLoadingCep(false);
      }
    } else {
      setLoadingCep(false);
    }
  }, 1000);

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ maxWidth: { md: '50%' } }}>
      <TextField
        id="cep"
        name="cep"
        label={t('campo.cep')}
        fullWidth
        required={required}
        value={cepValue}
        onBlur={handleBlur}
        onChange={(e) => onCepChange(formatCep(e.target.value))}
        inputProps={{ maxLength: 9, inputMode: 'numeric' }}
        error={error}
        helperText={helperText}
      />
      <LoadingButton
        loading={loadingCep}
        startIcon={<SearchRounded />}
        variant="contained"
        onClick={handleBuscarCep}
        sx={{ height: 56, flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {t('campo.search_cep')}
      </LoadingButton>
    </Stack>
  );
}
